import {
    AlertTriangle,
    ArrowRight,
    Briefcase,
    CheckCircle2,
    ChevronLeft,
    CircleDashed,
    LoaderCircle,
    MinusCircle,
    Search,
    Sparkles,
    UserPlus,
    Users,
    XCircle,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './BulkSalaryAssignmentScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const fmtAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const fmtDate = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const normalizeStaff = (response) => {
    const list = Array.isArray(response?.staff) ? response.staff : []
    return list.map((item) => ({
        staffIdentifier: item?.staffIdentifier,
        staffName: item?.staffName || 'Unnamed Staff',
        designationLabel: item?.designationLabel || 'Staff',
        salaryAssignment: item?.salaryAssignment || null,
    })).filter((item) => Boolean(item.staffIdentifier))
}

const BulkSalaryAssignmentScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [availableStaff, setAvailableStaff] = useState([])
    const [isLoadingStaff, setIsLoadingStaff] = useState(false)
    const [staffSearch, setStaffSearch] = useState('')

    const [templates, setTemplates] = useState([])
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

    const [staffSelections, setStaffSelections] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const [bulkResults, setBulkResults] = useState(null)

    const [commonTemplate, setCommonTemplate] = useState('')
    const [commonEffectiveFrom, setCommonEffectiveFrom] = useState(new Date().toISOString().split('T')[0])
    const [commonRemarks, setCommonRemarks] = useState('')

    const prefilledStaffIdentifier = location?.state?.prefillStaffIdentifier

    const fetchAvailableStaff = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingStaff(true)

            const response = await fetch(BASE_URL_API + 'getStoreStaffAssignmentOverview', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    statusFilter: 'ALL',
                    search: '',
                    page: 1,
                    perPage: 200,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()
            if (respo?.status === 'success') {
                setAvailableStaff(normalizeStaff(respo?.response || {}))
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch (error) {
            console.error('getStoreStaffAssignmentOverview error:', error)
        } finally {
            setIsLoadingStaff(false)
        }
    }, [authCtx, storeIdentifier])

    const fetchTemplates = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingTemplates(true)

            const response = await fetch(BASE_URL_API + 'getStoreSalaryStructureList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    showArchived: false,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()

            if (respo?.status === 'success') {
                const nextTemplates = Array.isArray(respo?.response?.templates)
                    ? respo.response.templates
                    : []
                setTemplates(nextTemplates)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch (error) {
            console.error('getStoreSalaryStructureList error:', error)
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchAvailableStaff()
        fetchTemplates()
    }, [fetchAvailableStaff, fetchTemplates])

    useEffect(() => {
        if (!prefilledStaffIdentifier || availableStaff.length === 0) return
        const found = availableStaff.find((staff) => staff.staffIdentifier === prefilledStaffIdentifier)
        if (!found) return
        setStaffSelections((prev) => {
            if (prev.some((entry) => entry.staffIdentifier === found.staffIdentifier)) return prev
            return [
                ...prev,
                {
                    staffIdentifier: found.staffIdentifier,
                    staffName: found.staffName,
                    designationLabel: found.designationLabel,
                    templateIdentifier: found.salaryAssignment?.templateIdentifier || '',
                    basicAmount: found.salaryAssignment?.basicAmount ? String(found.salaryAssignment.basicAmount) : '',
                    effectiveFrom: commonEffectiveFrom,
                    assignmentRemarks: '',
                    revisionReason: '',
                },
            ]
        })
    }, [prefilledStaffIdentifier, availableStaff, commonEffectiveFrom])

    const selectedIdSet = useMemo(() => new Set(staffSelections.map((entry) => entry.staffIdentifier)), [staffSelections])

    const filteredAvailableStaff = useMemo(() => {
        const q = staffSearch.trim().toLowerCase()
        return availableStaff.filter((staff) => {
            if (selectedIdSet.has(staff.staffIdentifier)) return false
            if (!q) return true
            const hay = `${staff.staffName} ${staff.designationLabel}`.toLowerCase()
            return hay.includes(q)
        })
    }, [availableStaff, selectedIdSet, staffSearch])

    const addStaffToSelection = (staff) => {
        setStaffSelections((prev) => {
            if (prev.some((entry) => entry.staffIdentifier === staff.staffIdentifier)) return prev
            if (prev.length >= 50) {
                toast.error('Maximum 50 staff can be selected in one batch.')
                return prev
            }

            return [
                ...prev,
                {
                    staffIdentifier: staff.staffIdentifier,
                    staffName: staff.staffName,
                    designationLabel: staff.designationLabel,
                    templateIdentifier: commonTemplate || staff.salaryAssignment?.templateIdentifier || '',
                    basicAmount: staff.salaryAssignment?.basicAmount
                        ? String(staff.salaryAssignment.basicAmount)
                        : '',
                    effectiveFrom: commonEffectiveFrom,
                    assignmentRemarks: commonRemarks || '',
                    revisionReason: '',
                },
            ]
        })
    }

    const removeStaff = (staffIdentifier) => {
        setStaffSelections((prev) => prev.filter((entry) => entry.staffIdentifier !== staffIdentifier))
    }

    const setStaffField = (index, key, value) => {
        setStaffSelections((prev) => {
            const next = [...prev]
            next[index] = { ...next[index], [key]: value }
            return next
        })
    }

    const applyCommonToAll = () => {
        if (staffSelections.length === 0) {
            toast.info('Select staff first.')
            return
        }

        setStaffSelections((prev) => prev.map((entry) => ({
            ...entry,
            templateIdentifier: commonTemplate || entry.templateIdentifier,
            effectiveFrom: commonEffectiveFrom || entry.effectiveFrom,
            assignmentRemarks: commonRemarks || entry.assignmentRemarks,
        })))
        toast.success('Common fields applied.')
    }

    const runBulkAssignment = async () => {
        if (!authCtx?.token || !storeIdentifier) return

        if (staffSelections.length === 0) {
            toast.error('Select at least one staff member.')
            return
        }

        if (staffSelections.length > 50) {
            toast.error('Maximum 50 staff assignments allowed per request.')
            return
        }

        const incomplete = staffSelections.filter(
            (entry) => !entry.templateIdentifier || !entry.basicAmount || Number(entry.basicAmount) <= 0 || !entry.assignmentRemarks?.trim()
        )

        if (incomplete.length > 0) {
            toast.error(`${incomplete.length} staff member(s) are missing required fields.`)
            return
        }

        try {
            setIsSaving(true)
            setBulkResults(null)

            const response = await fetch(BASE_URL_API + 'bulkAssignSalaryStructure', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    assignedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                    assignments: staffSelections.map((entry) => ({
                        staffIdentifier: entry.staffIdentifier,
                        templateIdentifier: entry.templateIdentifier,
                        basicAmount: parseFloat(entry.basicAmount) || 0,
                        effectiveFrom: entry.effectiveFrom,
                        assignmentRemarks: entry.assignmentRemarks.trim(),
                        revisionReason: (entry.revisionReason || '').trim(),
                    })),
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (respo?.status === 'error' && !respo?.response?.results) {
                toast.error(respo?.message || 'Bulk assignment failed.')
                return
            }

            setBulkResults(respo?.response || null)

            if (respo?.status === 'success') {
                toast.success(respo?.message || 'Bulk assignment complete.')
            } else if (respo?.status === 'partial') {
                toast.warning(respo?.message || 'Bulk assignment partially complete.')
            } else {
                toast.error(respo?.message || 'No assignments were saved.')
            }
        } catch (error) {
            console.error('bulkAssignSalaryStructure error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const resultItems = Array.isArray(bulkResults?.results) ? bulkResults.results : []

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Payroll</span>
                    <h1 className={styles.headerTitle}>Bulk Salary Assignment</h1>
                    <p className={styles.headerSub}>Assign salary structures to multiple staff in one action.</p>
                </div>
            </header>

            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={14} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>Bulk salary assignment is store-specific.</p>
                    </div>
                </section>
            )}

            <section className={styles.layoutGrid}>
                <div className={styles.leftPane}>
                    <section className={styles.panelCard}>
                        <div className={styles.panelTitle}><Users size={14} /> Available Staff</div>
                        <div className={styles.searchWrap}>
                            <Search size={14} className={styles.searchIcon} />
                            <input
                                className={styles.searchInput}
                                value={staffSearch}
                                onChange={(event) => setStaffSearch(event.target.value)}
                                placeholder={isLoadingStaff ? 'Loading staff…' : 'Search by name or designation'}
                            />
                        </div>

                        <div className={styles.staffListWrap}>
                            {isLoadingStaff ? (
                                <div className={styles.loaderBox}><LoaderCircle size={18} className={styles.spin} /> Loading staff…</div>
                            ) : filteredAvailableStaff.length === 0 ? (
                                <p className={styles.emptyText}>No staff available to add.</p>
                            ) : (
                                filteredAvailableStaff.map((staff) => (
                                    <button
                                        key={staff.staffIdentifier}
                                        type="button"
                                        className={styles.staffPickRow}
                                        onClick={() => addStaffToSelection(staff)}
                                    >
                                        <div>
                                            <p className={styles.staffName}>{staff.staffName}</p>
                                            <p className={styles.staffMeta}>{staff.designationLabel}</p>
                                        </div>
                                        <span className={styles.addChip}><UserPlus size={12} /> Add</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </section>
                </div>

                <div className={styles.rightPane}>
                    <section className={styles.panelCard}>
                        <div className={styles.panelTitle}><Briefcase size={14} /> Apply to All Selected Staff</div>

                        <div className={styles.fieldGrid}>
                            <div>
                                <label className={styles.fieldLabel}>Salary Structure (all)</label>
                                <select
                                    className={styles.selectInput}
                                    value={commonTemplate}
                                    onChange={(event) => setCommonTemplate(event.target.value)}
                                >
                                    <option value="">{isLoadingTemplates ? 'Loading templates…' : 'Leave unchanged'}</option>
                                    {templates.map((template) => (
                                        <option key={template.templateIdentifier} value={template.templateIdentifier}>
                                            {template.templateName}{template.designationLabel ? ` — ${template.designationLabel}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={styles.fieldLabel}>Effective From (all)</label>
                                <input
                                    className={styles.textInput}
                                    type="date"
                                    value={commonEffectiveFrom}
                                    onChange={(event) => setCommonEffectiveFrom(event.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={styles.fieldLabel}>Assignment Note (all)</label>
                            <input
                                className={styles.textInput}
                                value={commonRemarks}
                                onChange={(event) => setCommonRemarks(event.target.value)}
                                placeholder="e.g. Annual increment 2026"
                            />
                        </div>

                        <button type="button" className={styles.applyBtn} onClick={applyCommonToAll}>
                            Apply to All <FaArrowRightLong size={11} />
                        </button>
                    </section>

                    <section className={styles.panelCard}>
                        <div className={styles.panelTitle}>Selected Staff ({staffSelections.length})</div>

                        {staffSelections.length === 0 ? (
                            <p className={styles.emptyText}>Add staff from the left panel to begin bulk assignment.</p>
                        ) : (
                            <div className={styles.selectionRows}>
                                {staffSelections.map((staff, index) => (
                                    <div key={staff.staffIdentifier} className={styles.selectionCard}>
                                        <div className={styles.selectionTop}>
                                            <div>
                                                <p className={styles.staffName}>{staff.staffName}</p>
                                                <p className={styles.staffMeta}>{staff.designationLabel}</p>
                                            </div>
                                            <button type="button" className={styles.removeBtn} onClick={() => removeStaff(staff.staffIdentifier)}>
                                                <MinusCircle size={14} /> Remove
                                            </button>
                                        </div>

                                        <div className={styles.rowGrid}>
                                            <select
                                                className={`${styles.selectInput} ${!staff.templateIdentifier ? styles.inputError : ''}`}
                                                value={staff.templateIdentifier}
                                                onChange={(event) => setStaffField(index, 'templateIdentifier', event.target.value)}
                                            >
                                                <option value="">Select template *</option>
                                                {templates.map((template) => (
                                                    <option key={template.templateIdentifier} value={template.templateIdentifier}>
                                                        {template.templateName}
                                                    </option>
                                                ))}
                                            </select>

                                            <input
                                                className={`${styles.textInput} ${!staff.basicAmount ? styles.inputError : ''}`}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Basic ₹ *"
                                                value={staff.basicAmount || ''}
                                                onChange={(event) => setStaffField(index, 'basicAmount', event.target.value)}
                                            />

                                            <input
                                                className={styles.textInput}
                                                type="date"
                                                value={staff.effectiveFrom || ''}
                                                onChange={(event) => setStaffField(index, 'effectiveFrom', event.target.value)}
                                            />
                                        </div>

                                        <div className={styles.rowGridSecondary}>
                                            <input
                                                className={`${styles.textInput} ${!staff.assignmentRemarks?.trim() ? styles.inputError : ''}`}
                                                placeholder="Assignment note *"
                                                value={staff.assignmentRemarks || ''}
                                                onChange={(event) => setStaffField(index, 'assignmentRemarks', event.target.value)}
                                            />
                                            <input
                                                className={styles.textInput}
                                                placeholder="Detailed reason (optional)"
                                                value={staff.revisionReason || ''}
                                                onChange={(event) => setStaffField(index, 'revisionReason', event.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            type="button"
                            className={`${styles.saveBtn} ${(isSaving || staffSelections.length === 0) ? styles.saveBtnDisabled : ''}`}
                            onClick={runBulkAssignment}
                            disabled={isSaving || staffSelections.length === 0 || !storeIdentifier}
                        >
                            {isSaving
                                ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                                : <>Run Bulk Assignment <ArrowRight size={15} /></>}
                        </button>
                    </section>

                    {bulkResults && (
                        <section className={styles.resultsCard}>
                            <h3 className={styles.resultsTitle}>Bulk Assignment Complete</h3>

                            <div className={styles.resultCounts}>
                                <span className={`${styles.countBadge} ${styles.countSaved}`}>
                                    Saved {Number(bulkResults?.succeeded || 0)}
                                </span>
                                <span className={`${styles.countBadge} ${styles.countFailed}`}>
                                    Failed {Number(bulkResults?.failed || 0)}
                                </span>
                                <span className={`${styles.countBadge} ${styles.countSkipped}`}>
                                    Skipped {Number(bulkResults?.skipped || 0)}
                                </span>
                            </div>

                            <div className={styles.resultsList}>
                                {resultItems.map((result, index) => {
                                    const status = result?.status || 'skipped'
                                    const statusClass = status === 'success'
                                        ? styles.resultSuccess
                                        : status === 'failed'
                                            ? styles.resultFailed
                                            : styles.resultSkipped

                                    const StatusIcon = status === 'success'
                                        ? CheckCircle2
                                        : status === 'failed'
                                            ? XCircle
                                            : CircleDashed

                                    return (
                                        <div key={`${result?.staffIdentifier || result?.index || 'row'}-${index}`} className={`${styles.resultRow} ${statusClass}`}>
                                            <StatusIcon size={16} className={styles.resultIcon} />
                                            <div className={styles.resultInfo}>
                                                <p className={styles.resultName}>{result?.staffName || result?.staffIdentifier || `Item #${index + 1}`}</p>

                                                {status === 'success' ? (
                                                    <p className={styles.resultDetail}>
                                                        {result?.revisionType || 'UPDATED'} · {result?.templateName || 'Template'} · {fmtAmount(result?.basicAmount)} · from {fmtDate(result?.effectiveFrom)}
                                                    </p>
                                                ) : (
                                                    <p className={styles.resultError}>{result?.message || 'Could not process this item.'}</p>
                                                )}
                                            </div>

                                            {status === 'failed' && (
                                                <button
                                                    type="button"
                                                    className={styles.fixBtn}
                                                    onClick={() => navigate('/payroll/assign-salary-structure', {
                                                        state: {
                                                            staffIdentifier: result?.staffIdentifier,
                                                            staffName: result?.staffName,
                                                        },
                                                    })}
                                                >
                                                    Fix <FaArrowRightLong size={10} />
                                                </button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            <button type="button" className={styles.doneBtn} onClick={() => navigate(-1)}>
                                Done
                            </button>
                        </section>
                    )}
                </div>
            </section>
        </div>
    )
}

export default BulkSalaryAssignmentScreen